import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Pencil, X, Package, AlertTriangle, Zap, LineChart } from "lucide-react";
import ItemIntelligencePanel from "./ItemIntelligencePanel";

const CATEGORIES = [
  "Grains & Staples", "Spices & Masalas", "Vegetables",
  "Dairy & Proteins", "Oils & Condiments", "Bakery", "Beverages",
];
const CATEGORY_GROUP = {
  "Grains & Staples": "grocery",
  "Spices & Masalas": "grocery",
  "Vegetables": "grocery",
  "Bakery": "grocery",
  "Dairy & Proteins": "meat",
  "Oils & Condiments": "condiments",
  "Beverages": "beverages",
};
const UNITS = ["kg", "g", "litre", "ml", "pcs", "bunch", "bottle", "packet"];
const GROUPS = [
  { key: "all", label: "All" },
  { key: "grocery", label: "Grocery" },
  { key: "meat", label: "Meat & Dairy" },
  { key: "condiments", label: "Condiments" },
  { key: "beverages", label: "Beverages" },
];

const EMPTY = {
  name: "", category: "Grains & Staples", unit: "kg",
  stock_qty: 0, min_qty: 0, last_rate: 0, notes: "",
  // v2 extension fields (persisted via /ext PATCH)
  reorder_point: 0, reorder_qty: 0, preferred_vendor_id: "",
  pack_size: 0, pack_unit: "", hsn: "", gst_percent: 0,
};

export default function ItemsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState("all");
  const [search, setSearch] = useState("");
  const [showLow, setShowLow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [intelItem, setIntelItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (group !== "all") params.group = group;
      if (search) params.search = search;
      if (showLow) params.low_stock = true;
      const { data } = await api.get("/staff/inventory/items", { params });
      setItems(data);
    } catch {
      toast.error("Could not load items");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [group, showLow]);

  const grouped = useMemo(() => {
    const g = {};
    for (const i of items) {
      const c = i.category || "Other";
      (g[c] = g[c] || []).push(i);
    }
    return g;
  }, [items]);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (i) => { setEditing(i); setForm({ ...EMPTY, ...i }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, category: form.category, unit: form.unit, notes: form.notes,
        stock_qty: Number(form.stock_qty) || 0,
        min_qty: Number(form.min_qty) || 0,
        last_rate: Number(form.last_rate) || 0,
      };
      const extPatch = {
        reorder_point: Number(form.reorder_point) || 0,
        reorder_qty: Number(form.reorder_qty) || 0,
        preferred_vendor_id: form.preferred_vendor_id || "",
        pack_size: Number(form.pack_size) || 0,
        pack_unit: form.pack_unit || "",
        hsn: form.hsn || "",
        gst_percent: Number(form.gst_percent) || 0,
      };
      let saved;
      if (editing === "new") {
        const { data } = await api.post("/staff/inventory/items", payload);
        saved = data;
        // apply ext patch if any non-empty values
        if (Object.values(extPatch).some((v) => v)) {
          const { data: ext } = await api.patch(`/staff/inventory/items/${data.id}/ext`, extPatch);
          saved = ext;
        }
        setItems((p) => [...p, saved]);
        toast.success(`Added ${saved.name}`);
      } else {
        const { data } = await api.patch(`/staff/inventory/items/${editing.id}`, payload);
        saved = data;
        const { data: ext } = await api.patch(`/staff/inventory/items/${editing.id}/ext`, extPatch);
        saved = ext;
        setItems((p) => p.map((x) => (x.id === saved.id ? saved : x)));
        toast.success(`Updated ${saved.name}`);
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const q = search.toLowerCase();
  const filteredGrouped = useMemo(() => {
    const out = {};
    for (const [cat, list] of Object.entries(grouped)) {
      const l = q ? list.filter((i) => i.name.toLowerCase().includes(q)) : list;
      if (l.length) out[cat] = l;
    }
    return out;
  }, [grouped, q]);

  return (
    <div data-testid="items-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`item-group-${g.key}`}
              onClick={() => setGroup(g.key)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full transition-all ${
                group === g.key ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        <input
          data-testid="item-search"
          placeholder="Search item…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="tbb-inp text-sm w-56"
        />
        <label className="flex items-center gap-2 text-xs text-[hsl(var(--ink-soft))]">
          <input type="checkbox" data-testid="item-low-stock" checked={showLow} onChange={(e) => setShowLow(e.target.checked)} /> Low stock only
        </label>
        <button data-testid="item-backfill-rates" onClick={async () => {
          if (!window.confirm("Scan GRNs from the last 90 days and update every inventory item's rate?")) return;
          try {
            const { data } = await api.post("/staff/inventory/backfill-rates?days=90");
            toast.success(`Updated ${data.items_updated} item rate${data.items_updated === 1 ? "" : "s"} from ${data.grns_scanned} GRNs`);
            load();
          } catch (err) { toast.error(err?.response?.data?.detail || "Backfill failed"); }
        }} className="inline-flex items-center gap-1 text-sm px-3 py-2 border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] rounded hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors" title="Populate last_rate from recent approved GRNs">
          <Zap size={13} /> Backfill rates
        </button>
        <button data-testid="item-new" onClick={openNew} className="btn-brass !py-2 !px-4 text-sm">
          <Plus size={14} /> New item
        </button>
      </div>

      <div className="mt-6 space-y-6">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && Object.keys(filteredGrouped).length === 0 && (
          <div className="p-10 text-center text-[hsl(var(--ink-soft))] border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
            <Package className="mx-auto mb-2 opacity-40" size={28} />
            No items match your filters.
          </div>
        )}
        {!loading && Object.entries(filteredGrouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] mb-2">{cat} <span className="text-[hsl(var(--ink-soft))]">· {list.length}</span></div>
            <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
              <ul className="divide-y divide-[hsl(var(--rule))]">
                {list.map((it) => {
                  const lowStock = Number(it.min_qty) > 0 && Number(it.stock_qty) < Number(it.min_qty);
                  return (
                    <li key={it.id} data-testid={`item-${it.id}`} className="p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-3 items-center text-sm">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {it.name}
                          {lowStock && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]">
                              <AlertTriangle size={10} /> Low
                            </span>
                          )}
                        </div>
                        {it.notes && <div className="text-xs text-[hsl(var(--ink-soft))]">{it.notes}</div>}
                      </div>
                      <div className="text-xs text-[hsl(var(--ink-soft))]">{it.unit}</div>
                      <div className="text-xs">
                        <span className="text-[hsl(var(--ink-soft))]">Stock:</span> <span className="font-semibold">{Number(it.stock_qty).toLocaleString("en-IN")}</span> {it.unit}
                      </div>
                      <div className="text-xs">
                        <span className="text-[hsl(var(--ink-soft))]">Last rate:</span> ₹{Number(it.last_rate).toLocaleString("en-IN")}
                      </div>
                      <button data-testid={`item-intel-${it.id}`} onClick={() => setIntelItem(it)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors" title="Price intelligence — trends, benchmarks, recommendations">
                        <LineChart size={12} /> Price intel
                      </button>
                      <button data-testid={`item-edit-${it.id}`} onClick={() => openEdit(it)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--rule))]">
                        <Pencil size={12} /> Edit
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="item-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-xl p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{editing === "new" ? "New item" : "Edit item"}</div>
                <h2 className="mt-1 text-2xl">{form.name || "—"}</h2>
                <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">Group: <span className="text-[hsl(var(--brass-2))]">{CATEGORY_GROUP[form.category] || "other"}</span></div>
              </div>
              <button type="button" onClick={close} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Name *
                <input required data-testid="item-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Category *
                <select data-testid="item-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tbb-inp mt-1 w-full">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Unit *
                <select data-testid="item-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="tbb-inp mt-1 w-full">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Current stock
                <input type="number" min="0" step="0.01" data-testid="item-form-stock" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Min qty (reorder alert)
                <input type="number" min="0" step="0.01" data-testid="item-form-min" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Last rate (₹)
                <input type="number" min="0" step="0.01" value={form.last_rate} onChange={(e) => setForm({ ...form, last_rate: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Reorder point
                <input type="number" min="0" step="0.01" data-testid="item-form-reorder-point" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Reorder qty (auto-PO qty)
                <input type="number" min="0" step="0.01" data-testid="item-form-reorder-qty" value={form.reorder_qty} onChange={(e) => setForm({ ...form, reorder_qty: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Pack size
                <input type="number" min="0" step="0.01" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Pack unit (e.g. pkt of kg)
                <input value={form.pack_unit} onChange={(e) => setForm({ ...form, pack_unit: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="pkt / bottle / crate" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                HSN code
                <input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                GST %
                <input type="number" min="0" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={close} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="item-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving..." : "Save item"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
      {intelItem && (
        <ItemIntelligencePanel
          itemName={intelItem.name}
          itemId={intelItem.id}
          unit={intelItem.unit}
          onClose={() => setIntelItem(null)}
        />
      )}
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
